/**
 * Validador de URLs e Links
 * 
 * Centraliza todas as validações relacionadas a URLs, links e conexões.
 * 
 * @module url.validator
 */

import type { UrlValidationOptions, OriginValidationOptions } from '../types/security.types';

/**
 * Valida se uma URL é válida e segura
 * 
 * Valida o formato da URL usando o construtor URL nativo do JavaScript,
 * remove barras finais e garante que a URL está bem formada.
 * 
 * @param url - URL a ser validada
 * @param options - Opções de validação (opcional)
 * @returns URL normalizada e validada (sem trailing slashes)
 * @throws Error se a URL for inválida
 * 
 * @example
 * ```typescript
 * validateApiUrl('https://corel.one1tec.com.br') // 'https://corel.one1tec.com.br'
 * validateApiUrl('http://localhost:3333/') // 'http://localhost:3333'
 * validateApiUrl('invalid-url') // throws Error
 * ```
 */
export function validateApiUrl(url: string, options?: UrlValidationOptions): string {
  if (!url || typeof url !== 'string') {
    throw new Error(`URL da API inválida: ${url} (deve ser uma string não vazia)`);
  }

  try {
    // Valida se é uma URL válida usando o construtor URL
    const urlObj = new URL(url);
    
    // Validar protocolo se especificado
    if (options?.allowedProtocols && !options.allowedProtocols.includes(urlObj.protocol)) {
      throw new Error(`Protocolo não permitido: ${urlObj.protocol}. Permitidos: ${options.allowedProtocols.join(', ')}`);
    }
    
    // Validar se requer HTTPS
    if (options?.requireHttps && urlObj.protocol !== 'https:') {
      throw new Error('URL deve usar HTTPS');
    }
    
    // Validar domínios permitidos (whitelist)
    if (options?.allowedDomains && options.allowedDomains.length > 0) {
      const hostname = urlObj.hostname;
      const isAllowed = options.allowedDomains.some(domain => {
        // Suporta wildcards como *.example.com
        if (domain.startsWith('*.')) {
          const baseDomain = domain.slice(2);
          return hostname === baseDomain || hostname.endsWith('.' + baseDomain);
        }
        return hostname === domain;
      });
      
      if (!isAllowed) {
        throw new Error(`Domínio não permitido: ${hostname}. Permitidos: ${options.allowedDomains.join(', ')}`);
      }
    }
    
    // Validar localhost se necessário
    if (options?.allowLocalhost === false && 
        (urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1')) {
      throw new Error('Localhost não é permitido nesta configuração');
    }
    
    // Remover trailing slashes
    return url.replace(/\/+$/, '');
  } catch (error) {
    if (error instanceof TypeError) {
      // Erro do construtor URL (URL inválida)
      console.error('URL da API inválida:', url, error);
      throw new Error(`URL da API inválida: ${url}`);
    }
    // Re-throw erros de validação customizados
    throw error;
  }
}

/**
 * Valida se uma origem é permitida
 * 
 * Verifica se uma origem (origin) é permitida baseado em várias regras:
 * - Origens vazias/null são permitidas (WebView local)
 * - Mesma origem (window.location.origin) é sempre permitida
 * - IPs da rede local podem ser permitidos se configurado
 * - Lista de origens permitidas (whitelist)
 * 
 * @param origin - Origem a ser validada (ex: 'https://example.com')
 * @param options - Opções de validação (opcional)
 * @returns true se a origem é permitida, false caso contrário
 * 
 * @example
 * ```typescript
 * isAllowedOrigin('https://corel.one1tec.com.br') // true (se na whitelist)
 * isAllowedOrigin('null') // true (WebView local)
 * isAllowedOrigin('https://malicious.com') // false
 * ```
 */
export function isAllowedOrigin(
  origin: string,
  options?: OriginValidationOptions
): boolean {
  // Origem vazia ou null indica WebView/iframe local (C# WebView2)
  if (!origin || origin === 'null') {
    return true;
  }

  // Mesma origem é sempre permitida (se configurado)
  if (options?.allowSameOrigin !== false && typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }

  // Origens permitidas em desenvolvimento
  const allowedOrigins = options?.allowedOrigins || ['*'];
  
  // Se '*' está na lista, permitir todas
  if (allowedOrigins.includes('*')) {
    return true;
  }

  // Permitir IPs da rede local (192.168.x.x) para intranet
  if (options?.allowLocalNetwork !== false && typeof window !== 'undefined' && window.location) {
    const currentHost = window.location.hostname;
    
    // Se estiver acessando via IP da rede local, permitir origens da mesma rede
    if (currentHost.startsWith('192.168.') || 
        currentHost.startsWith('10.') || 
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(currentHost)) {
      const originHost = origin.replace(/^https?:\/\//, '').split(':')[0];
      
      // Permitir se for da mesma rede local
      if (originHost.startsWith('192.168.') || 
          originHost.startsWith('10.') || 
          /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(originHost)) {
        return true;
      }
    }
  }

  // Verificar se está na lista de origens permitidas
  return allowedOrigins.includes(origin);
}

/**
 * Valida se uma string é uma URL válida (sem normalizar)
 * 
 * @param url - String a ser validada
 * @returns true se é uma URL válida, false caso contrário
 * 
 * @example
 * ```typescript
 * isValidUrl('https://example.com') // true
 * isValidUrl('not-a-url') // false
 * ```
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

